import { AsyncActionButton, Constants, EditWorksheetDto, FilteredCreditApplication, Loader, reportErrorToConsole, useMerchantById, useUpdateWorksheet } from '@applyonline/shared'
import { EFinancingProgram, EPaymentFrequency, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import {
  formatCurrencyNoDecimals,
  formatCurrencyRoundedUpToNoDecimals,
  formatCurrencyToNearestUpperMultipleToNoDecimals,
} from '@if/core/utils'
import { useGetPromotionalCreditAppPlan } from '../../../api/compute-api'
import { ManageCoappButton } from '../../../components'

import { FormEventHandler, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { useTermAmountOptions } from '../../../data/credit-application-hooks'
import { getLoanAmount, getPrequalifiedMaxTerm, hasFlexPlan, HasZeroFeeAndZeroInterestPromotion } from '../../../data/credit-application-selectors'
import { useTrackStepReachedEvent } from '../../../services/trackingEvents'
import { ETrackedEvents } from '../../../types/tracking'
import ApprovedSoftHitAndHardHitFooter from '../components/ApprovedSoftHitAndHardHitFooter'
import useTermPaymentsTable from './hooks'
interface Props {
  creditApp: FilteredCreditApplication
}

const InitialApprobationForm = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const params = useParams()
  const id = params.id!
  const [monthlyPaymentPerMonths, isLoadingTermTable] = useTermPaymentsTable(creditApp, {
    requestedAmount: getLoanAmount(creditApp),
  })
  const initialPrequalifiedTerm = getPrequalifiedMaxTerm(creditApp)
  const [merchant, isLoadingMerchant] = useMerchantById(creditApp.merchantId, creditApp.financingProgramId)
  const [promotionalPlan, isLoadingPromoAmounts] = useGetPromotionalCreditAppPlan(
    getLoanAmount(creditApp),
    id,
    creditApp.merchantPaymentPlanId,
  )
  const maxLoanTerm: number = creditApp.merchantPaymentPlanId
    ? (promotionalPlan?.loanTerm ?? 0)
    : (initialPrequalifiedTerm ?? 0)

  const [submitWorksheetUpdate, isSubmitting] = useUpdateWorksheet(EFinancingProgram.Personal)
  const termOptions = useTermAmountOptions(monthlyPaymentPerMonths)
  useTrackStepReachedEvent(creditApp, ETrackedEvents.QualifSuccess)

  const hasZeroFeeAndZeroInterest = HasZeroFeeAndZeroInterestPromotion(creditApp)
  const hasAdminFee =
    creditApp.merchantPaymentPlan?.borrowerFeeRate && creditApp.merchantPaymentPlan?.borrowerFeeRate > 0
  const exponent = hasAdminFee ? '1' : ''
  const hasFlex = hasFlexPlan(creditApp)
  const promotionText = hasFlex ? 'decision.hardhitDecisionApprovedFlex' : 'decision.hardhitDecisionApprovedFlash'
  const hasCoapplicant = creditApp.coapplicant != null
  const hasChanceForPromotionalPlan =
    (merchant?.hasPlanPromoRules && creditApp.merchantPaymentPlan === null) ||
    (creditApp.prequalPromoCode != null && creditApp.merchantPaymentPlan === null)
  const canAddCoapp =
    creditApp.consentHardHit === true &&
    creditApp.initialRequestedLoanAmount > creditApp.finalDecision.maxAmountFinanced &&
    !Constants.MerchantsThatForbidHavingCoapplicants.includes(creditApp.merchantId)

  const submitTerm: FormEventHandler = (e) => {
    e.preventDefault()
    if (creditApp) {
      const term =
        creditApp.merchantPaymentPlan !== null
          ? creditApp.merchantPaymentPlan.loanTerm
          : parseInt(termOptions[termOptions.length - 1].id, 10)

      const dto: EditWorksheetDto = {
        term,
        amountRequested: getLoanAmount(creditApp),
        creditApplicationId: creditApp.id,
        paymentFrequency: EPaymentFrequency.Monthly,
        deliveryOn: null,
        firstPaymentOn: null,
        includeInsurance: false,
        status: EWorksheetStatus.Draft,
        paymentPlanId: creditApp.merchantPaymentPlanId || EPaymentPlan.RegularDailyInterests,
        create: creditApp.worksheet === null,
        versionTag: creditApp.worksheet?.versionTag ?? null,
      }
      submitWorksheetUpdate(dto).catch(reportErrorToConsole)
    }
  }
  const getTitle = useMemo<string>(() => {
    const hasSkipIncomeVerification =
      creditApp.applicant.hasSkippedIncomeValidation &&
      (creditApp.coapplicant != null ? creditApp.coapplicant.hasSkippedIncomeValidation : true)
    return hasSkipIncomeVerification
      ? t('decision.hardhitDecisionApprovedTitle')
      : t('decision.hardhitDecisionConditionallyApprovedTitle')
  }, [creditApp, t])

  if (isLoadingPromoAmounts || isLoadingTermTable || !creditApp || isLoadingMerchant)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  const requestedAmountReached = creditApp.requestedLoanAmount <= creditApp.finalDecision.maxAmountFinanced
  const loanAmount = Math.min(creditApp.requestedLoanAmount, creditApp.finalDecision.maxAmountFinanced)

  return (
    <main className="general-message" style={{ display: 'block' }}>
      <form onSubmit={submitTerm}>
        <div id="loan-form-container">
          <h3>
            {requestedAmountReached && (
              <>
                <i className="fa-solid fa-party-horn" /> {t('common.congratulation')}{' '}
              </>
            )}
            {getTitle}
          </h3>
          <div className="paragraph">
            {creditApp.merchantPaymentPlan !== null && promotionalPlan?.paymentForFrequency && (
              <p>
                <Trans
                  i18nKey={promotionText}
                  values={{
                    maximumAmount: formatCurrencyNoDecimals(creditApp.finalDecision.maxAmountFinanced),
                    reducedInterestRate: creditApp.merchantPaymentPlan.reducedInterestRate,
                    reducedRateDurationInMonths: creditApp.merchantPaymentPlan.reducedRateDurationInMonths,
                  }}
                />
                !{hasFlex && <sup style={{ fontSize: '1rem', color: 'red' }}>1</sup>}
                &nbsp;
                <Trans
                  i18nKey="decision.estimatedPayment"
                  values={{
                    monthlyPayment: formatCurrencyToNearestUpperMultipleToNoDecimals(
                      promotionalPlan.paymentForFrequency,
                      hasZeroFeeAndZeroInterest ? 1 : 5,
                    ),
                  }}
                />
                .{hasFlex && hasAdminFee && <sup style={{ fontSize: '1rem', color: 'red' }}>2</sup>}
                {!hasFlex && exponent && hasAdminFee && <sup style={{ fontSize: '1rem', color: 'red' }}>1</sup>}
              </p>
            )}

            {creditApp.merchantPaymentPlan === null &&
              monthlyPaymentPerMonths &&
              monthlyPaymentPerMonths.length > 0 && (
                <>
                  <p style={{ textAlign: 'center' }}>
                    <Trans
                      i18nKey="decision.hardhitDecisionApproved"
                      values={{
                        maximumAmount: formatCurrencyNoDecimals(creditApp.finalDecision.maxAmountFinanced),
                        minPaymentAmount: formatCurrencyNoDecimals(
                          monthlyPaymentPerMonths[monthlyPaymentPerMonths.length - 1].amountWithoutInsurance,
                        ),
                      }}
                    />
                  </p>
                  {canAddCoapp && !hasChanceForPromotionalPlan && (
                    <div style={{ marginTop: '32px', marginBottom: '32px' }}>
                      <div className="strong">{t('prequalification.addCoapplicant.title')}</div>
                      <div className="btn-group">
                        <ManageCoappButton creditAppId={creditApp.id} useChangeLabel={hasCoapplicant} merchantId={creditApp.merchantId} isHardDecline={creditApp.finalDecision.isHardDecline}/>
                      </div>
                    </div>
                  )}
                  {hasChanceForPromotionalPlan && <div className="strong">{t('common.regularPlanQualified')}</div>}
                </>
              )}
          </div>
          <div>
            <div className="btn-group mobile-reversed justify-end" style={{ marginTop: '32px' }}>
              {hasChanceForPromotionalPlan && (
                <ManageCoappButton creditAppId={creditApp.id} useChangeLabel={hasCoapplicant} merchantId={creditApp.merchantId} isHardDecline={creditApp.finalDecision.isHardDecline}/>
              )}
              <AsyncActionButton type="submit" className="btn btn-blue" isPending={isSubmitting} primary>
                <span>
                  {requestedAmountReached
                    ? t('common.imInterested')
                    : t('common.continueWith', { amount: formatCurrencyRoundedUpToNoDecimals(loanAmount) })}
                </span>
                <i className="fa-regular fa-arrow-right" />
              </AsyncActionButton>
            </div>
            <div className="paragraph">
              <p style={{ textAlign: 'center' }}>
                <br />
                <Trans
                  i18nKey="loan.youHaveQuestions"
                  values={{
                    phoneNumber: '1-888-689-9876',
                  }}
                  components={{ a: <Link to="tel:18886899876" style={{ textWrap: 'nowrap' }} /> }}
                />
              </p>
            </div>
          </div>
          <div style={{ marginTop: '4rem' }}>
            <ApprovedSoftHitAndHardHitFooter
              merchantPaymentPlan={creditApp.merchantPaymentPlan}
              minInterestRate={creditApp.finalDecision.interestRate}
              hasCoapplicant={hasCoapplicant}
              maxLoanTerm={maxLoanTerm}
            />
          </div>
        </div>
      </form>
    </main>
  )
}

export default InitialApprobationForm
